/**
 * NPT-2175. Drive-time helpers for the hotel search page.
 *
 * Drive times come from the API (`hotel.driveTimeSeconds` / `driveDistanceMeters`, NPT-2174). They
 * are shown rounded, never as an exact figure, because they are routed on a typical-day graph and
 * not a live estimate.
 */

const METERS_PER_MILE = 1609.344;
/** Ordering-only estimate: road distance is ~1.4x the straight line, at ~13.9 m/s (50 km/h). */
const ROAD_FACTOR = 1.4;
const AVERAGE_SPEED_MPS = 13.9;

export const UNDER_TEN_MINUTES_SECONDS = 600;
/** First poll delay; each later round waits 1.5x longer, up to DRIVE_TIME_POLL_MAX_INTERVAL_MS. */
export const DRIVE_TIME_POLL_INTERVAL_MS = 1500;
export const DRIVE_TIME_POLL_MAX_INTERVAL_MS = 4000;
export const DRIVE_TIME_POLL_BACKOFF = 1.5;
export const DRIVE_TIME_POLL_TIMEOUT_MS = 20000;
/**
 * Ids per drive-times poll request, and the API's cap (DRIVE_TIMES_MAX_HOTEL_IDS, NPT-2174). The ids
 * travel in the query string, and the stg/prod API WAF blocks query strings over 2,048 bytes
 * (CommonRuleSet SizeRestrictions_QUERYSTRING): 200 seven-digit ids are ~1,600 bytes.
 */
export const DRIVE_TIME_POLL_CHUNK = 200;

/** Query params for one `GET /hotel/drive-times` request. */
export const driveTimesParams = (geohash: string, hotelIds: string[]) => ({ geohash, hotelIds: hotelIds.join(',') });

/** `ids` split into poll requests of at most DRIVE_TIME_POLL_CHUNK. */
export function chunkPollIds(ids: string[], size = DRIVE_TIME_POLL_CHUNK): string[][] {
   const chunks: string[][] = [];
   for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size));
   return chunks;
}

/**
 * Delay before the next poll, after `rounds` answered polls (1.5 s, 2.25 s, 3.4 s, then 4 s). The
 * bounds are arguments so the hook passes the module constants, which the specs shorten.
 */
export function driveTimePollDelay(rounds: number, base: number, max: number): number {
   return Math.min(base * DRIVE_TIME_POLL_BACKOFF ** Math.max(0, rounds - 1), max);
}

/** Mapbox feature types that get drive times. Everything else keeps the distance label. */
export const DRIVE_TIME_FEATURE_TYPES = ['address', 'poi'];

export type DriveTimeStatus = 'ready' | 'pending' | 'unavailable';

export type DriveTimeAnchor = { geohash: string; type: 'venue' | 'address' | 'poi'; name: string | null };

export type DriveTimeMeta = {
   status: DriveTimeStatus;
   sortAvailable: boolean;
   anchor: DriveTimeAnchor | null;
};

export const NO_DRIVE_TIME: DriveTimeMeta = { status: 'unavailable', sortAvailable: false, anchor: null };

export type DriveTimeDisplay = { kind: 'under' } | { kind: 'approx'; minutes: number };

/**
 * Under 10 minutes collapses to "Under 10 min"; otherwise minutes round to the nearest 5
 * (720 s -> 10, 1380 s -> 25, 38 min -> 40). Null for a hotel without a usable drive time.
 */
export function getDriveTimeDisplay(seconds: number | null | undefined): DriveTimeDisplay | null {
   if (seconds == null || !Number.isFinite(Number(seconds)) || Number(seconds) < 0) return null;
   if (Number(seconds) < UNDER_TEN_MINUTES_SECONDS) return { kind: 'under' };
   return { kind: 'approx', minutes: Math.round(Number(seconds) / 60 / 5) * 5 };
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** The card label, or null when the hotel has no drive time (the caller falls back to distance). */
export function formatDriveTimeLabel(seconds: number | null | undefined, place: string | null | undefined, t: TranslateFn): string | null {
   const display = getDriveTimeDisplay(seconds);
   if (!display) return null;
   const name = place?.trim();
   const base = display.kind === 'under' ? 'hotel.card.driveTime.under' : 'hotel.card.driveTime.approx';
   const key = name ? base : `${base}NoPlace`;
   return t(key, display.kind === 'under' ? { place: name } : { minutes: display.minutes, place: name });
}

/** The slice of a search-list item these helpers read. */
type DriveTimeItem = { hotel?: { driveTimeSeconds?: number | null; distance_miles?: number | string | null } | null } | null;

export const hasDriveTime = (item: DriveTimeItem | undefined): boolean => item?.hotel?.driveTimeSeconds != null;

/** What `useDriveTimePoll` hands back: hotel id -> routed time. */
export type PolledDriveTimes = Record<string, { seconds: number; meters: number }>;

/**
 * Fills in polled drive times for hotels the serp left without one, keeping the list's order. A
 * no-op (same array) until the poll has something. The season planner uses it; the search page
 * still carries its own copy (NPT-2175).
 */
export function mergeDriveTimes<T extends { id?: string | number; hotel?: { id?: string | number; driveTimeSeconds?: number | null } | null }>(
   list: T[],
   times: PolledDriveTimes,
): T[] {
   if (!list.length || Object.keys(times).length === 0) return list;
   return list.map(h => {
      const found = hasDriveTime(h) ? undefined : times[String(h?.hotel?.id ?? h?.id)];
      return found ? ({ ...h, hotel: { ...h.hotel, driveTimeSeconds: found.seconds, driveDistanceMeters: found.meters } } as T) : h;
   });
}

/** Ids of the hotels in `list` that have no drive time yet, for `useDriveTimePoll`. */
export function missingDriveTimeIds(
   list: readonly ({ id?: string | number; hotel?: { id?: string | number; driveTimeSeconds?: number | null } | null } | null | undefined)[],
): string[] {
   return list
      .filter(h => !hasDriveTime(h))
      .map(h => h?.hotel?.id ?? h?.id)
      .filter((id): id is string | number => id != null && id !== '')
      .map(String);
}

/**
 * Sort key for the Drive time sort. A hotel the API has no drive time for (mixed lists happen:
 * the pipeline could not route it) is ordered by a straight-line estimate so a 3-minute-away
 * hotel is not exiled behind a 40-minute one. Never displayed. A hotel with neither sorts last.
 */
export function driveTimeSortKey(item: DriveTimeItem | undefined): number {
   const seconds = item?.hotel?.driveTimeSeconds;
   if (seconds != null && Number.isFinite(Number(seconds))) return Number(seconds);
   const miles = Number(item?.hotel?.distance_miles);
   // adaptSerpToListItem collapses a missing distance to 0, which means "unplaceable" here.
   if (!Number.isFinite(miles) || miles <= 0) return Infinity;
   return (miles * METERS_PER_MILE * ROAD_FACTOR) / AVERAGE_SPEED_MPS;
}

/** Cheap stable hash for a React Query key; the id list can be ~1000 long. */
export function hashIds(ids: string[]): string {
   const joined = [...ids].sort().join(',');
   let h = 5381;
   for (let i = 0; i < joined.length; i++) h = ((h << 5) + h + joined.charCodeAt(i)) | 0;
   return `${ids.length}:${(h >>> 0).toString(36)}`;
}

/** ISO-2 country of a Mapbox feature, from the `context` the location picker hands back. */
type CountryCarrier = { country?: { country_code?: string }; context?: { country?: { country_code?: string } } } | null | undefined;

export function getLocationCountry(...sources: CountryCarrier[]): string | undefined {
   for (const s of sources) {
      const code = s?.country?.country_code ?? s?.context?.country?.country_code;
      if (code && typeof code === 'string') return code.toUpperCase();
   }
   return undefined;
}
